import { useMemo } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useDataService } from '@/lib/service-context';
import { useResilientMutation } from '@/lib/use-resilient-mutation';
import { listCacheFallback } from '@/lib/list-cache-fallback';
import type {
  WithholdingCertificate,
  WithholdingCertificateFilters,
  CreateWithholdingCertificateInput,
  UpdateWithholdingCertificateInput,
  WithholdingDeclaration,
  WithholdingDeclarationFilters,
  CreateWithholdingDeclarationInput,
} from '@erp/data-access/interfaces';

const KEY = ['withholding'] as const;
const CERTS_BASE = [...KEY, 'certificates'] as const;
const DECLS_BASE = [...KEY, 'declarations'] as const;

// ─── Operation Types ──────────────────────────────────────────────────────────

export function useRSOperationTypes(applicable?: 'sale' | 'purchase') {
  const ds = useDataService();
  return useQuery({
    queryKey: [...KEY, 'operation-types', applicable],
    queryFn: () => ds.withholding.listOperationTypes(applicable ? { applicable } : undefined),
    staleTime: 5 * 60 * 1000, // Reference data — stable
  });
}

// ─── Certificates ─────────────────────────────────────────────────────────────

/**
 * Always fetches the FULL certificates list (canonical key) and filters
 * in memory so all pages — sales/purchases sub-tabs, third-party
 * profiles — read the prewarmed cache offline.
 */
export function useWithholdingCertificates(filters?: WithholdingCertificateFilters) {
  const ds = useDataService();
  const query = useQuery({
    queryKey: [...CERTS_BASE, undefined],
    queryFn: () => ds.withholding.listCertificates(),
  });
  const filtered = useMemo(() => {
    if (!query.data) return query.data;
    if (!filters) return query.data;
    const { certificateType, thirdPartyId, dateFrom, dateTo, declarationId, isPaid } = filters;
    return query.data.filter((c) => {
      if (certificateType && c.certificateType !== certificateType) return false;
      if (thirdPartyId && c.thirdPartyId !== thirdPartyId) return false;
      if (dateFrom && c.datePayment < dateFrom) return false;
      if (dateTo && c.datePayment > dateTo) return false;
      if (declarationId !== undefined && c.declarationId !== declarationId) return false;
      if (isPaid !== undefined) {
        const paid = c.paymentId !== null;
        if (paid !== isPaid) return false;
      }
      return true;
    });
  }, [query.data, filters]);
  return { ...query, data: filtered } as typeof query;
}

export function useWithholdingCertificate(id: string) {
  const ds = useDataService();
  const qc = useQueryClient();
  return useQuery({
    queryKey: [...KEY, 'certificate', id],
    queryFn: () => ds.withholding.getCertificateById(id),
    enabled: !!id,
    placeholderData: () => listCacheFallback<WithholdingCertificate>(qc, CERTS_BASE, id),
  });
}

export function useCertificateBySource(sourceDocumentId: string) {
  const ds = useDataService();
  return useQuery({
    queryKey: [...KEY, 'certificate-by-source', sourceDocumentId],
    queryFn: () => ds.withholding.getCertificateBySourceId(sourceDocumentId),
    enabled: !!sourceDocumentId,
  });
}

export function useCreateCertificate() {
  const ds = useDataService();
  return useResilientMutation<CreateWithholdingCertificateInput, WithholdingCertificate>({
    entity: 'withholding_certificates',
    op: 'create',
    invalidate: [KEY],
    online: (input) => ds.withholding.createCertificate(input),
    optimistic: (input, clientId) =>
      ({
        id: clientId,
        ...input,
        certificateNumber: `OFFLINE-${clientId.slice(0, 6).toUpperCase()}`,
        declarationId: null,
        paymentId: null,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      }) as WithholdingCertificate,
    applyOptimistic: (qc, certificate) => {
      qc.setQueriesData<WithholdingCertificate[]>({ queryKey: CERTS_BASE }, (old) =>
        Array.isArray(old) && !old.some((c) => c.id === certificate.id) ? [certificate, ...old] : old,
      );
    },
  });
}

export function useUpdateCertificate() {
  const ds = useDataService();
  return useResilientMutation<{ id: string; input: UpdateWithholdingCertificateInput }, WithholdingCertificate>({
    entity: 'withholding_certificates',
    op: 'update',
    invalidate: [KEY],
    online: ({ id, input }) => ds.withholding.updateCertificate(id, input),
    optimistic: ({ id, input }) => ({ id, ...input, updatedAt: new Date().toISOString() }) as WithholdingCertificate,
    applyOptimistic: (qc, _r, { id, input }) => {
      qc.setQueriesData<WithholdingCertificate[]>({ queryKey: CERTS_BASE }, (old) =>
        Array.isArray(old) ? old.map((c) => (c.id === id ? ({ ...c, ...input } as WithholdingCertificate) : c)) : old,
      );
    },
  });
}

export function useDeleteCertificate() {
  const ds = useDataService();
  return useResilientMutation<string, void>({
    entity: 'withholding_certificates',
    op: 'delete',
    invalidate: [KEY],
    online: (id) => ds.withholding.deleteCertificate(id),
    optimistic: () => undefined as unknown as void,
    applyOptimistic: (qc, _r, id) => {
      qc.setQueriesData<WithholdingCertificate[]>({ queryKey: CERTS_BASE }, (old) =>
        Array.isArray(old) ? old.filter((c) => c.id !== id) : old,
      );
    },
  });
}

// ─── Declarations ─────────────────────────────────────────────────────────────

export function useWithholdingDeclarations(filters?: WithholdingDeclarationFilters) {
  const ds = useDataService();
  const query = useQuery({
    queryKey: [...DECLS_BASE, undefined],
    queryFn: () => ds.withholding.listDeclarations(),
  });
  const filtered = useMemo(() => {
    if (!query.data) return query.data;
    if (!filters) return query.data;
    const { declarationType, anneeDepot, moisDepot, isExported } = filters;
    return query.data.filter((d) => {
      if (declarationType && d.declarationType !== declarationType) return false;
      if (anneeDepot && d.anneeDepot !== anneeDepot) return false;
      if (moisDepot && d.moisDepot !== moisDepot) return false;
      if (isExported !== undefined && d.isExported !== isExported) return false;
      return true;
    });
  }, [query.data, filters]);
  return { ...query, data: filtered } as typeof query;
}

export function useWithholdingDeclaration(id: string) {
  const ds = useDataService();
  const qc = useQueryClient();
  return useQuery({
    queryKey: [...KEY, 'declaration', id],
    queryFn: () => ds.withholding.getDeclarationById(id),
    enabled: !!id,
    placeholderData: () => listCacheFallback<WithholdingDeclaration>(qc, DECLS_BASE, id),
  });
}

export function useCreateDeclaration() {
  const ds = useDataService();
  return useResilientMutation<CreateWithholdingDeclarationInput, WithholdingDeclaration>({
    entity: 'withholding_declarations',
    op: 'create',
    invalidate: [KEY],
    online: (input) => ds.withholding.createDeclaration(input),
    optimistic: (input, clientId) =>
      ({
        id: clientId,
        ...input,
        declarationNumber: `OFFLINE-${clientId.slice(0, 6).toUpperCase()}`,
        isExported: false,
        xmlContent: null,
        exportedAt: null,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      }) as WithholdingDeclaration,
    applyOptimistic: (qc, declaration) => {
      qc.setQueriesData<WithholdingDeclaration[]>({ queryKey: DECLS_BASE }, (old) =>
        Array.isArray(old) && !old.some((d) => d.id === declaration.id) ? [declaration, ...old] : old,
      );
    },
  });
}

export function useMarkDeclarationExported() {
  const ds = useDataService();
  return useResilientMutation<{ id: string; xmlContent: string; declarantMatricule: string }, void>({
    entity: 'withholding_declarations',
    op: 'custom',
    method: 'markExported',
    invalidate: [KEY],
    online: ({ id, xmlContent, declarantMatricule }) =>
      ds.withholding.markDeclarationExported(id, xmlContent, declarantMatricule),
    optimistic: () => undefined as unknown as void,
    applyOptimistic: (qc, _r, { id, xmlContent }) => {
      qc.setQueriesData<WithholdingDeclaration[]>({ queryKey: DECLS_BASE }, (old) =>
        Array.isArray(old)
          ? old.map((d) => (d.id === id ? { ...d, isExported: true, xmlContent, exportedAt: new Date().toISOString() } : d))
          : old,
      );
    },
  });
}

export function useDeleteDeclaration() {
  const ds = useDataService();
  return useResilientMutation<string, void>({
    entity: 'withholding_declarations',
    op: 'delete',
    invalidate: [KEY],
    online: (id) => ds.withholding.deleteDeclaration(id),
    optimistic: () => undefined as unknown as void,
    applyOptimistic: (qc, _r, id) => {
      qc.setQueriesData<WithholdingDeclaration[]>({ queryKey: DECLS_BASE }, (old) =>
        Array.isArray(old) ? old.filter((d) => d.id !== id) : old,
      );
    },
  });
}
